import { ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useVisualViewportInset } from '@/hooks/useVisualViewportInset';

interface GlassModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Required for a11y. Rendered visually-hidden unless `showTitle` is true. */
	title: string;
	/** Required for a11y. Rendered visually-hidden unless `showDescription` is true. */
	description?: string;
	/** Desktop max width in px. Defaults to 440. */
	desktopMaxWidth?: number;
	/** Override mobile height cap (dvh). Defaults to 85. Ignored when mobileFullScreen. */
	mobileMaxHeightDvh?: number;
	/** Show the X close button on desktop only (mobile dismisses via backdrop tap). */
	showCloseOnDesktop?: boolean;
	/** Mobile fills viewport edge-to-edge (top to bottom) and renders an X close in the top-right. */
	mobileFullScreen?: boolean;
	className?: string;
	bodyClassName?: string;
	children: ReactNode;
}

/**
 * Shared glass-band modal. Same Radix-dialog-centered surface on mobile and
 * desktop, full screen width on mobile (square edges, top/bottom hairlines)
 * and a centered 440px rounded card on desktop. Page imagery shows through
 * above and below the panel on mobile because vertical padding is preserved
 * by the `85dvh` body cap.
 *
 * Designed to replace mobile bottom-sheet form modals (offers, reviews,
 * reports, etc.) where the host page imagery is part of the experience.
 *
 * Mirrors the AuthSheet pattern. See AuthSheet for the canonical reference.
 */
export const GlassModal = ({
	open,
	onOpenChange,
	title,
	description,
	desktopMaxWidth = 440,
	mobileMaxHeightDvh = 85,
	showCloseOnDesktop = true,
	mobileFullScreen = false,
	className,
	bodyClassName,
	children,
}: GlassModalProps) => {
	const { keyboardHeight } = useVisualViewportInset();

	return (
		<DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay
					className={cn(
						'fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px]',
						'data-[state=open]:animate-in data-[state=closed]:animate-out',
						'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
					)}
				/>
				<DialogPrimitive.Content
					style={{ ['--gm-max' as string]: `${desktopMaxWidth}px` }}
					className={cn(
						mobileFullScreen
							? 'fixed inset-0 md:left-1/2 md:top-1/2 md:right-auto md:bottom-auto md:-translate-x-1/2 md:-translate-y-1/2 z-50'
							: 'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
						mobileFullScreen
							? 'w-screen h-[100dvh] md:h-auto md:w-[calc(100vw-1.5rem)] md:max-w-[var(--gm-max)]'
							: 'w-screen md:w-[calc(100vw-1.5rem)] md:max-w-[var(--gm-max)]',
						'overflow-hidden rounded-none md:rounded-2xl',
						mobileFullScreen ? 'border-0 md:border' : 'border-y md:border',
						'bg-black/55 backdrop-blur-2xl border-white/[0.1] shadow-2xl',
						'shadow-[inset_0_1px_1px_rgba(255,255,255,0.04)] text-foreground',
						'duration-200 ease-out',
						'data-[state=open]:animate-in data-[state=closed]:animate-out',
						'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
						mobileFullScreen
							? 'md:data-[state=closed]:zoom-out-95 md:data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-bottom-4 data-[state=open]:slide-in-from-bottom-4'
							: 'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
						className,
					)}
				>
					<VisuallyHidden>
						<DialogPrimitive.Title>{title}</DialogPrimitive.Title>
						{description ? (
							<DialogPrimitive.Description>{description}</DialogPrimitive.Description>
						) : null}
					</VisuallyHidden>

					{showCloseOnDesktop || mobileFullScreen ? (
						<DialogPrimitive.Close
							type="button"
							aria-label="Close"
							style={mobileFullScreen ? { top: 'calc(env(safe-area-inset-top) + 0.5rem)' } : undefined}
							className={cn(
								'absolute right-3 z-10 h-9 w-9 items-center justify-center',
								mobileFullScreen ? 'inline-flex md:top-3' : 'top-3 hidden md:inline-flex',
								'rounded-full text-foreground/70 hover:text-foreground',
								'hover:bg-white/[0.06] active:bg-white/[0.1]',
								'focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30',
								'[-webkit-tap-highlight-color:transparent]',
							)}
						>
							<X className="h-5 w-5" />
						</DialogPrimitive.Close>
					) : null}

					<div
						className={cn(
							'overflow-y-auto overscroll-contain px-6 pt-7 pb-6',
							bodyClassName,
						)}
						style={{
							maxHeight: mobileFullScreen
								? keyboardHeight
									? `calc(100dvh - ${keyboardHeight}px)`
									: '100dvh'
								: keyboardHeight
									? `calc(100dvh - ${keyboardHeight}px - 24px)`
									: `${mobileMaxHeightDvh}dvh`,
							paddingTop: mobileFullScreen
								? 'calc(env(safe-area-inset-top) + 3.25rem)'
								: undefined,
							paddingBottom: mobileFullScreen
								? keyboardHeight
									? '1.5rem'
									: 'calc(env(safe-area-inset-bottom) + 1.5rem)'
								: keyboardHeight ? '1.5rem' : undefined,
						}}
					>
						{children}
					</div>
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
};

export default GlassModal;
